import { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ComposedChart, Area,
} from "recharts";
import api from "@/lib/api";
import { fmtCompact, fmtNum, RATING_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const COLORS = { blue: "#2563EB", green: "#10B981", amber: "#F59E0B", red: "#EF4444", slate: "#64748B" };

function ChartCard({ title, children }) {
  return (
    <Card className="p-5">
      <h4 className="font-heading font-semibold text-sm mb-4">{title}</h4>
      <div className="h-64">{children}</div>
    </Card>
  );
}

const tickStyle = { fontSize: 11, fontFamily: "JetBrains Mono", fill: "hsl(var(--muted-foreground))" };

export function ConfrontoTab({ companyId }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/companies/${companyId}/comparison`)
      .then((r) => setSeries(r.data.series))
      .finally(() => setLoading(false));
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
      </div>
    );
  }

  if (series.length < 2) {
    return (
      <Card className="p-12 text-center border-dashed">
        <p className="text-sm text-muted-foreground">
          Servono almeno due esercizi per il confronto pluriennale. Carica altri bilanci XBRL di questa azienda.
        </p>
      </Card>
    );
  }

  const tooltipStyle = {
    contentStyle: {
      background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
      borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12,
    },
    formatter: (v) => fmtNum(v),
  };

  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="rating-trend">
        <h4 className="font-heading font-semibold text-sm mb-4">Rating d'Impresa per Esercizio</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {series.map((s) => {
            const meta = RATING_META[s.rating] || RATING_META.D;
            return (
              <div key={s.year} data-testid={`rating-year-${s.year}`} className={`rounded-lg border p-3 flex items-center gap-3 ${meta.box}`}>
                <div className={`font-heading font-bold text-3xl leading-none ${meta.text}`}>{s.rating}</div>
                <div className="min-w-0">
                  <div className="font-mono text-sm font-semibold">{s.year}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{s.rating_score}/100 · {s.rating_label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard title="Ricavi & Valore della Produzione">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="year" tick={tickStyle} />
            <YAxis tick={tickStyle} tickFormatter={fmtCompact} width={70} />
            <RTooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="ricavi" name="Ricavi" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="valore_produzione" name="Valore Produzione" stroke={COLORS.amber} strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="EBITDA, EBIT & Utile Netto">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="year" tick={tickStyle} />
            <YAxis tick={tickStyle} tickFormatter={fmtCompact} width={70} />
            <RTooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="ebitda" name="EBITDA" fill={COLORS.green} radius={[4, 4, 0, 0]} />
            <Bar dataKey="ebit" name="EBIT" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
            <Bar dataKey="utile" name="Utile" fill={COLORS.amber} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Struttura Finanziaria: PFN & Patrimonio Netto">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="year" tick={tickStyle} />
            <YAxis tick={tickStyle} tickFormatter={fmtCompact} width={70} />
            <RTooltip {...tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="patrimonio_netto" name="Patrimonio Netto" stroke={COLORS.green} fill={COLORS.green} fillOpacity={0.15} strokeWidth={2} />
            <Line type="monotone" dataKey="pfn" name="PFN" stroke={COLORS.red} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="debiti_finanziari" name="Debiti Finanziari" stroke={COLORS.slate} strokeWidth={2} strokeDasharray="4 4" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Redditività: EBITDA Margin % & ROE %">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="year" tick={tickStyle} />
            <YAxis tick={tickStyle} tickFormatter={(v) => `${v}%`} width={45} />
            <RTooltip {...tooltipStyle} formatter={(v) => `${fmtNum(v, 1)}%`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="ebitda_margin" name="EBITDA Margin" stroke={COLORS.blue} strokeWidth={2.5} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="roe" name="ROE" stroke={COLORS.green} strokeWidth={2.5} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
      </div>
    </div>
  );
}
